import encodeQR from "qr";
import { qrPath } from "./qrPath";

/**
 * The encode, shared by everything that draws a code.
 *
 * `ui/QrCode` renders one into the page; the same panel's *Copy image* rasterises
 * one onto the clipboard. Both go through here so they cannot drift: an operator
 * who copies the picture beside connector 2 must get connector 2's code, and two
 * call sites each holding their own error-correction level is how that stops being
 * true without anything looking wrong.
 */

/**
 * ISO/IEC 18004's quiet zone: four modules of light on every side, without which a
 * decoder cannot find the symbol's edge. It looks like too much white space and it
 * is not optional — a code cropped to its own bounds is one that fails when the
 * sticker is applied next to something dark, or when the copied PNG is pasted onto
 * a coloured slide.
 */
export const QUIET_ZONE_MODULES = 4;

/**
 * Medium recovers ~15% of a damaged symbol. The right level for a label on
 * hardware people plug cables into: `low` is for screens, and `high`/`quartile`
 * grow the symbol enough to cost printed resolution at sticker size.
 */
export const ERROR_CORRECTION = "medium" as const;

/**
 * Literal black on literal white — the highest-contrast symbol a scanner can be
 * given, and the only colours safe in a document that leaves this stylesheet.
 *
 * `ui/QrCode` paints the in-page code with the design tokens instead. These are for
 * the standalone file: a `var()` that cannot resolve is invalid at computed-value
 * time and paints black, so a token-coloured document rasterises to a solid black
 * square once separated from the panel — background included.
 */
export const QR_DARK = "#000000";
export const QR_LIGHT = "#ffffff";

/**
 * A copied code is pasted into a work order, a chat message, a label template — all
 * of which resample it. Enough pixels that the modules survive being scaled down by
 * whatever receives them, and small enough that the clipboard payload stays under a
 * few hundred kB.
 */
export const MINIMUM_RASTER_EDGE_PX = 1024;

export interface QrSymbol {
  /** Edge length in modules, quiet zone included. */
  readonly modules: number;
  /** The dark modules, in module units, as one SVG path. */
  readonly path: string;
}

/** `undefined` when the value is past the largest symbol's capacity. */
export function qrSymbol(value: string): QrSymbol | undefined {
  let bitmap: boolean[][];
  try {
    bitmap = encodeQR(value, "raw", { ecc: ERROR_CORRECTION, border: QUIET_ZONE_MODULES });
  } catch {
    return undefined;
  }

  const modules = bitmap.length;
  if (modules === 0) return undefined;

  return { modules, path: qrPath(bitmap) };
}

/**
 * A raster size that is a whole multiple of the module grid.
 *
 * Not cosmetic. At a size that is not a multiple, each module covers a fractional
 * number of pixels, the rasteriser resamples, and the module edges come out as grey
 * — which is the state a decoder is least able to threshold. Rounding up to the next
 * whole multiple costs a few percent of file size and makes every module an exact
 * block of identical pixels.
 */
export function rasterEdgePx(modules: number): number {
  const scale = Math.max(1, Math.ceil(MINIMUM_RASTER_EDGE_PX / modules));
  return scale * modules;
}

/**
 * The symbol as a standalone SVG document, for handing to a rasteriser.
 *
 * Assembled as a string rather than serialised out of the DOM because the rendered
 * element carries this application's design tokens and a serialised copy of it is
 * exactly the black square described above. Nothing here interpolates caller text:
 * the path is digits and path commands generated from the bitmap, and the encoded
 * value never appears in the markup.
 */
export function qrSvgDocument(symbol: QrSymbol, edgePx: number): string {
  const { modules, path } = symbol;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${edgePx}" height="${edgePx}" ` +
    `viewBox="0 0 ${modules} ${modules}" shape-rendering="crispEdges">` +
    `<rect width="${modules}" height="${modules}" fill="${QR_LIGHT}"/>` +
    `<path d="${path}" fill="${QR_DARK}"/>` +
    `</svg>`
  );
}
